import React from 'react'
import classNames from 'classnames'

import { EditorialStory } from '../../elements/EditorialStory'
import { Text } from '../../elements/Text'
import { containerMaxWidth, mq } from '../../utils'
import { useSpacing } from 'marketing-site/src/library/providers/spacingProvider'
import { IProps } from './index'

export const EditorialStoryGrid = ({
  cards,
  heading,
  subtitle,
  sidebarHeading,
  asFullGrid,
  asBiggerCard,
}: IProps): React.ReactElement => {
  const spacing = useSpacing()

  const classes = classNames('editorial-story-grid', {
    'editorial-story-grid--full-grid': asFullGrid,
    'editorial-story-grid--semi-full-grid': asBiggerCard,
    'editorial-story-grid--padding': !spacing,
  })

  return (
    <div className={classes}>
      {(heading || subtitle) && (
        <div className="editorial-story-grid__header">
          {heading && (
            <h2 className="editorial-story-grid__heading">
              <Text brand="default" size="heading-2">
                {heading}
              </Text>
            </h2>
          )}
          {subtitle && (
            <div className="editorial-story-grid__subtitle">
              <Text brand="default" size="heading-4">
                {subtitle}
              </Text>
            </div>
          )}
        </div>
      )}
      <ul className="editorial-story-grid__body">
        {cards.map((cardProps, index) => (
          <li key={cardProps.title} className="editorial-story-grid__card">
            {index === 1 && sidebarHeading && (
              <div className="editorial-story-grid__sidebar-heading" aria-hidden="true">
                {sidebarHeading}
              </div>
            )}
            <EditorialStory {...cardProps} big={index === 0} asBiggerCard={asBiggerCard} />
          </li>
        ))}
      </ul>

      <style jsx>
        {`
          .editorial-story-grid {
            padding-left: 2rem;
            padding-right: 2rem;

            &--padding {
              padding-top: 3.5rem;
              padding-bottom: 3.5rem;
            }

            &__heading {
              text-align: center;
              margin-bottom: 1.25rem;
            }

            &__subtitle {
              max-width: 15rem;
              margin: 0 auto;
              margin-bottom: 2rem;
              text-align: center;
              word-break: break-word;
            }

            &__header + &__body {
              margin-top: 2.5rem;
            }

            &__body {
              display: grid;
              grid-gap: 3rem;
              justify-items: center;
              align-items: start;
            }

            &__card {
              grid-column: 1/-1;
              width: 100%;
              position: relative;
            }

            &__sidebar-heading {
              /* custom fixed-size type treatment */
              font-size: 1.75rem;
              font-weight: bold;
              margin-bottom: 1.75rem;
            }

            &--full-grid .editorial-story-grid__body {
              grid-template-columns: 1fr;
            }
          }

          @media (${mq.tablet}) {
            .editorial-story-grid {
              padding-left: 4.6rem;
              padding-right: 4.6rem;

              &--padding {
                padding-top: 5rem;
                padding-bottom: 5rem;
              }

              &__heading {
                text-align: center;
              }

              &__header + &__body {
                margin-top: 4rem;
              }

              &__subtitle {
                max-width: 40rem;
                margin-top: 2rem;
              }

              &__body {
                grid-template-columns: 5fr 6fr;
                grid-template-rows: auto repeat(${cards.length - 2}, 1fr);
                grid-column-gap: 8.25rem;
                grid-row-gap: 5rem;
              }

              &__sidebar-heading {
                margin-bottom: 2.5rem;
              }

              &__card + &__card:not(:nth-child(2))::before {
                content: '';
                width: 100%;
                height: 1px;
                position: absolute;
                top: -2.5rem;
                background-color: $slate;
              }
            }
          }

          @media (${mq.desktop}) {
            .editorial-story-grid {
              max-width: ${containerMaxWidth};
              margin: 0 auto;

              &--padding {
                padding-top: 6.25rem;
                padding-bottom: 6.25rem;
              }

              &__card {
                :first-child {
                  grid-column: 1;
                  grid-row: 1 / span ${cards.length - 1};
                }

                &:nth-child(2) {
                  grid-column: 2;
                }

                &:nth-child(3) {
                  grid-column: 2;
                }

                &:nth-child(4) {
                  grid-column: 2;
                }
              }
            }
          }
        `}
      </style>
    </div>
  )
}
